// src/features/locker-assignments/components/AccessHistoryPopover.tsx

import React, { useState } from 'react';
import {
  IconButton,
  Popover,
  Box,
  Typography,
  Chip,
  CircularProgress,
  List,
  ListItem,
  Divider,
} from '@mui/material';
import {
  History as HistoryIcon,
  Lock as LockIcon,
} from '@mui/icons-material';
import { useTranslate, useNotify, useDataProvider } from 'react-admin';

interface AccessLogEntry {
  id: string;
  action: 'Opened' | 'Closed' | 'Denied';
  timestamp: string;
  lockerId?: string;
  lockerDisplayNumber?: string;
  tagCode?: string;
  tagType?: string;
  userName?: string;
  details?: Record<string, any>;
}

interface AccessHistoryPopoverProps {
  assignmentId: string;
  accessCount: number;
  lastAccess?: string;
}

export const AccessHistoryPopover: React.FC<AccessHistoryPopoverProps> = ({
  assignmentId,
  accessCount,
  lastAccess,
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const dataProvider = useDataProvider();
  
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [accessLogs, setAccessLogs] = useState<AccessLogEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const handleClick = async (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
    
    // Load access history if not already loaded
    if (!loaded && accessCount > 0) {
      setLoading(true);
      try {
        // Use custom API call for access logs
        const { data } = await dataProvider.getAssignmentAccessLogs(assignmentId, 1, 20);
       
        
        // Transform the data if needed based on your backend response
        if (data && Array.isArray(data)) {
          setAccessLogs(data);
        } else if (data && data.logs) {
          setAccessLogs(data.logs);
        } else {
          // Mock data for development - remove when backend is ready
          const mockLogs: AccessLogEntry[] = [
            {
              id: '1',
              action: 'Opened',
              timestamp: '2025-09-12T11:36:00Z',
              lockerDisplayNumber: 'H207',
              tagCode: '47840605',
              tagType: 'AUTO_NUMERIC',
              userName: 'Asita-3',
            },
            {
              id: '2',
              action: 'Opened',
              timestamp: '2025-09-12T09:16:00Z',
              lockerDisplayNumber: 'H207',
              tagCode: '47840605',
              tagType: 'AUTO_NUMERIC',
              userName: 'Asita-1',
            },
            {
              id: '3',
              action: 'Opened',
              timestamp: '2025-09-11T14:00:00Z',
              lockerDisplayNumber: 'H207',
              tagCode: '47840605',
              tagType: 'AUTO_NUMERIC',
              userName: 'Control_1',
            },
          ];
          setAccessLogs(mockLogs);
        }
        
        setLoaded(true);
      } catch (error) {
        console.error('Error loading access history:', error);
        notify(translate('resources.locker-assignments.access_history.error_loading'), { type: 'error' });
      } finally {
        setLoading(false);
      }
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const getActionChipColor = (action: string) => {
    switch (action) {
      case 'Opened':
        return 'success';
      case 'Closed':
        return 'info';
      case 'Denied':
        return 'error';
      default:
        return 'default';
    }
  };

  const open = Boolean(anchorEl);
  const id = open ? 'access-history-popover' : undefined;

  return (
    <>
      <IconButton
        size="small"
        onClick={handleClick}
        aria-describedby={id}
        sx={{ 
          ml: 0.5,
          color: accessCount > 0 ? 'action.active' : 'action.disabled',
        }}
      >
        <HistoryIcon fontSize="small" />
      </IconButton>

      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        slotProps={{
          paper: {
            sx: { 
              maxWidth: 400, 
              minWidth: 350,
              maxHeight: 500,
            }
          }
        }}
      >
        <Box sx={{ p: 2 }}>
          {/* Header */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 0.5 }}>
              {translate('resources.locker-assignments.access_history.title')}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                {accessCount} {translate('resources.locker-assignments.access_history.accesses')}
              </Typography>
              {lastAccess && (
                <>
                  <Typography variant="body2" color="text.secondary">
                    {translate('resources.locker-assignments.access_history.last')}:
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {formatDate(lastAccess)}
                  </Typography>
                </>
              )}
            </Box>
          </Box>

          <Divider sx={{ mb: 2 }} />

          {/* Access Log List */}
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={24} />
            </Box>
          ) : accessCount === 0 ? (
            <Box sx={{ py: 2, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                {translate('resources.locker-assignments.access_history.no_access')}
              </Typography>
            </Box>
          ) : accessLogs.length === 0 ? (
            <Box sx={{ py: 2, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                {translate('resources.locker-assignments.access_history.loading_error')}
              </Typography>
            </Box>
          ) : (
            <List dense sx={{ py: 0 }}>
              {accessLogs.map((log, index) => (
                <ListItem
                  key={log.id}
                  sx={{ 
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    py: 1.5,
                    px: 0,
                    borderBottom: index < accessLogs.length - 1 ? '1px solid' : 'none',
                    borderColor: 'divider',
                  }}
                >
                  {/* First row: Action chip and timestamp */}
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 1,
                    width: '100%',
                    mb: 0.5
                  }}>
                    <Chip
                      label={translate(`resources.locker-assignments.access_history.action.${log.action.toLowerCase()}`)}
                      size="small"
                      color={getActionChipColor(log.action)}
                      sx={{ 
                        fontSize: '0.75rem',
                        height: 24,
                      }}
                    />
                    <Typography variant="body2" color="text.secondary">
                      {formatDate(log.timestamp)}
                    </Typography>
                  </Box>

                  {/* Second row: Locker and user info */}
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 1.5,
                    width: '100%',
                    pl: 1
                  }}>
                    {log.lockerDisplayNumber && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <LockIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                          {log.lockerDisplayNumber}
                        </Typography>
                      </Box>
                    )}
                    
                    {log.tagCode && (
                      <>
                        <Typography variant="body2" color="text.secondary">•</Typography>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                          {log.tagCode}
                        </Typography>
                      </>
                    )}
                    
                    {log.userName && (
                      <>
                        <Typography variant="body2" color="text.secondary">•</Typography>
                        <Typography variant="body2">
                          {log.userName}
                        </Typography>
                      </>
                    )}
                  </Box>

                  {/* Third row: Tag type if exists */}
                  {log.tagType && (
                    <Typography 
                      variant="caption" 
                      color="text.secondary"
                      sx={{ pl: 1, mt: 0.5 }}
                    >
                      {log.tagType} tag
                    </Typography>
                  )}
                </ListItem>
              ))}
            </List>
          )}

          {/* Show more button if there are more logs */}
          {loaded && accessLogs.length > 0 && accessLogs.length < accessCount && (
            <Box sx={{ mt: 2, textAlign: 'center' }}>
              <Typography 
                variant="caption" 
                color="text.secondary"
                sx={{ fontStyle: 'italic' }}
              >
                {translate('resources.locker-assignments.access_history.showing_recent', { 
                  shown: accessLogs.length, 
                  total: accessCount 
                })}
              </Typography>
            </Box>
          )}
        </Box>
      </Popover>
    </>
  );
};

export default AccessHistoryPopover;